// The authenticated shell: responsive sidebar with role-filtered navigation,
// collapsible desktop mode, mobile overlay mode, and user menu.

import {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  NavLink,
  Outlet,
  useLocation,
} from 'react-router-dom';

import {
  useAuth,
} from '../auth';

import {
  requestNavRefresh,
} from '../refresh';

import {
  useTheme,
  themeGlyph,
} from '../theme';

import {
  cn,
  Icon,
  type IconName,
} from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: 'home',
  },
  {
    to: '/sessions',
    label: 'Recordings',
    icon: 'recordings',
  },
  {
    to: '/upload',
    label: 'Upload & Process',
    icon: 'upload',
  },
  {
    to: '/algorithms',
    label: 'Algorithm Scripts',
    icon: 'code',
    adminOnly: true,
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: 'gear',
  },
];

export default function Layout() {
  const {
    me,
    isAdmin,
    signOut,
  } = useAuth();

  const {
    mode,
    toggle,
  } = useTheme();

  const {
    pathname,
  } = useLocation();

  const [menuOpen, setMenuOpen] =
    useState(false);

  const [navOpen, setNavOpen] =
    useState(false);

  const menuRef =
    useRef<HTMLDivElement>(null);

  /*
   * Reset scrolling whenever the route changes.
   *
   * The page scrolls inside #root (see index.css), not the window; reset both,
   * plus anything marked as a scroll container. The second frame catches a
   * route whose content mounts a moment after the pathname changes.
   */
  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo(0, 0);
      document
        .querySelectorAll<HTMLElement>('#root, main, [data-scroll-container]')
        .forEach((node) => {
          node.scrollTop = 0;
          node.scrollLeft = 0;
        });
    };

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    resetScroll();
    const frame = window.requestAnimationFrame(resetScroll);
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);

  /*
   * Close the profile menu when clicking outside it
   * or pressing Escape.
   */
  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const onDown = (
      event: MouseEvent,
    ) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(
          event.target as Node,
        )
      ) {
        setMenuOpen(false);
      }
    };

    const onKey = (
      event: KeyboardEvent,
    ) => {
      if (
        event.key === 'Escape'
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      onDown,
    );

    document.addEventListener(
      'keydown',
      onKey,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        onDown,
      );

      document.removeEventListener(
        'keydown',
        onKey,
      );
    };
  }, [menuOpen]);

  /*
   * When returning to desktop, close the mobile drawer.
   */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 700) {
        setNavOpen(false);
      }
    };

    window.addEventListener(
      'resize',
      onResize,
    );

    return () => {
      window.removeEventListener(
        'resize',
        onResize,
      );
    };
  }, []);

  const items = NAV.filter(
    (item) =>
      !item.adminOnly ||
      isAdmin,
  );


  function closeMenus() {
    setMenuOpen(false);
    setNavOpen(false);
  }

  function handleNavClick(
    item: NavItem,
  ) {
    closeMenus();

    /*
     * If the user clicks the page they are already on, React Router
     * does not navigate anywhere. Ask the current screen to refresh
     * instead.
     */
    if (
      pathname === item.to ||
      pathname.startsWith(
        `${item.to}/`,
      )
    ) {
      requestNavRefresh();
    }
  }

  function handleUserMenuClick() {
    /*
     * This controls the profile menu only.
     * It must not toggle navOpen.
     */
    setMenuOpen(
      (current) => !current,
    );
  }

  return (
    // Top-navigation shell: the legacy left sidebar is intentionally not rendered.
    <div className="relative z-1 block min-h-full overflow-x-hidden dark:z-2 from-901:in-[.nxtq-chatbot-expanded]:mr-[360px] from-901:in-[.nxtq-chatbot-expanded]:w-[calc(100%-360px)]">
      <header className="sticky inset-x-0 top-0 z-80 flex h-[82px] items-center">
        <div className="mx-auto flex h-[82px] min-h-[78px] w-[min(100%,1400px)] max-w-none min-w-0 items-center gap-6 px-7 upto-900:min-h-[70px] upto-900:px-[22px] upto-700:min-h-16">

          <NavLink
            to="/dashboard"
            className="mr-auto flex shrink-0 items-center gap-0 text-fg no-underline decoration-current hover:text-fg hover:no-underline"
            aria-label="nxtCloud dashboard"
            onClick={() => {
              setNavOpen(false);

              if (pathname === '/dashboard') {
                requestNavRefresh();
              }
            }}
          >
            <span
              className="hidden shrink-0 place-items-center text-primary"
              aria-hidden="true"
            >
              <svg viewBox="0 0 32 32" width="28" height="28">
                <rect
                  width="32"
                  height="32"
                  rx="8"
                  fill="currentColor"
                />
                <path
                  d="M7 20c2.4-7 4.6-9 6.6-9s2.2 7 4.2 7 3-4.5 6.2-4.5"
                  stroke="var(--surface)"
                  strokeWidth="2.4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>

            <span className="font-heading text-[23px] leading-none font-medium tracking-[0.105em] whitespace-nowrap upto-700:text-[20px]">
              nxt
              <span className="text-accent">Cloud</span>
            </span>
          </NavLink>

          <nav
            className={cn(
              'relative inset-0 z-5 ml-auto mr-[22px] flex min-w-0 shrink-0 items-center justify-end gap-[25px] overflow-x-auto overflow-y-hidden [scrollbar-width:none] upto-1000:mr-4 upto-1000:gap-[18px] upto-700:hidden [&::-webkit-scrollbar]:hidden',
              // Phones: the nav opens as a panel under the bar from the menu button.
              navOpen &&
                'upto-700:absolute upto-700:inset-auto upto-700:top-[calc(100%+8px)] upto-700:right-3 upto-700:left-3 upto-700:z-90 upto-700:m-0 upto-700:flex upto-700:flex-col upto-700:items-stretch upto-700:gap-1 upto-700:rounded-[12px] upto-700:border upto-700:border-edge upto-700:bg-mist upto-700:p-2 upto-700:shadow-[0_24px_70px_rgba(14,26,43,0.16)] dark:upto-700:bg-surface dark:upto-700:shadow-[0_30px_80px_rgba(0,0,0,0.5)]',
            )}
            aria-label="Primary navigation"
          >
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'relative inset-0 flex h-[42px] min-h-[42px] shrink-0 items-center justify-center gap-0 py-2 font-sans text-[13px] font-medium whitespace-nowrap no-underline decoration-current hover:no-underline',
                    isActive
                      ? "px-2 py-0 text-fg hover:text-fg after:absolute after:right-2 after:bottom-0.5 after:left-2 after:h-0.5 after:bg-aqua after:content-['']"
                      : 'text-steel dark:text-fg-2 dark:hover:text-fg',
                    'upto-700:h-auto upto-700:min-h-11 upto-700:justify-start upto-700:rounded-[9px] upto-700:px-3 upto-700:py-2.5 upto-700:text-[14px] upto-700:after:hidden',
                    isActive && 'upto-700:bg-accent-soft',
                  )
                }
                onClick={() => handleNavClick(item)}
              >
                <span className="hidden">
                  <Icon name={item.icon} />
                </span>
                <span className="block min-w-0 truncate">
                  {item.label}
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="ml-0.5 flex shrink-0 items-center gap-0 upto-700:gap-2">
            <div
              className="relative z-2000 flex min-h-[42px] min-w-[42px] shrink-0 items-center justify-center"
              ref={menuRef}
            >
              {menuOpen && (
                <div
                  className="absolute top-[calc(100%+10px)] right-0 z-3000 block w-[280px] max-w-[calc(100vw-24px)] min-w-[280px] rounded-[14px] border border-edge bg-mist p-2 dark:bg-surface dark:shadow-[0_30px_80px_rgba(0,0,0,0.5)] upto-700:w-[min(280px,calc(100vw-24px))] upto-700:min-w-0"
                  role="menu"
                >
                  <div className="flex items-center gap-[11px] px-2.5 pt-2.5 pb-3">
                    <span className="grid size-[38px] shrink-0 place-items-center rounded-[50%] bg-primary-soft text-primary">
                      <Icon name="user" />
                    </span>
                    <span className="flex min-w-0 flex-col leading-[1.25]">
                      <strong className="block truncate text-[14px] font-bold text-fg">
                        {me?.displayName ||
                          me?.email?.split('@')[0] ||
                          'Signed in'}
                      </strong>
                      <small className="mt-[3px] block truncate text-[12px] leading-[1.4] text-fg-2">{me?.email}</small>
                    </span>
                  </div>

                  <div className="mx-1 mt-0.5 mb-1.5 h-px bg-edge" />

                  <button
                    type="button"
                    className="relative z-301 flex min-h-10 w-full items-center gap-2.5 rounded-[9px] px-2.5 py-[9px] text-left cursor-pointer bg-transparent text-fg hover:bg-surface-2"
                    role="menuitem"
                    onClick={() => {
                      toggle();
                      setMenuOpen(false);
                    }}
                  >
                    <span className="flex w-5 shrink-0 basis-5 items-center justify-center justify-items-center">
                      {themeGlyph(mode)}
                    </span>
                    <span>
                      {mode === 'dark'
                        ? 'Light theme'
                        : 'Dark theme'}
                    </span>
                  </button>

                  <button
                    type="button"
                    className="relative z-301 flex min-h-10 w-full items-center gap-2.5 rounded-[9px] px-2.5 py-[9px] text-left cursor-pointer mt-0.5 border border-[#dc2626] bg-[#dc2626] text-white hover:border-[#b91c1c] hover:bg-[#b91c1c] [:root:not([data-theme=dark])_&]:hover:brightness-[.94]"
                    role="menuitem"
                    onClick={() => {
  setMenuOpen(false);
  void signOut();
  
}}
                  >
                    <span className="flex w-5 shrink-0 basis-5 items-center justify-center justify-items-center">
                      <Icon name="signout" />
                    </span>
                    <span>Sign out</span>
                  </button>
                </div>
              )}

              <button
                type="button"
                className="m-0 grid size-[42px] min-h-[38px] min-w-[38px] cursor-pointer place-items-center rounded-[50%] border border-aqua/20 bg-accent-soft p-0 text-aqua hover:border-edge hover:bg-surface-2 hover:text-fg dark:hover:text-aqua"
                aria-label="Open profile menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={handleUserMenuClick}
              >
                <span
                  className="grid size-[34px] min-h-[34px] min-w-[34px] flex-[0_0_34px] place-items-center rounded-[50%] bg-primary-soft text-primary"
                  aria-hidden="true"
                >
                  <Icon name="user" />
                </span>
              </button>
            </div>

            <button
              type="button"
              className="hidden size-10 cursor-pointer place-items-center rounded-[9px] border border-edge bg-surface text-[20px] leading-none text-fg-2 hover:border-edge-strong hover:bg-surface-2 hover:text-primary upto-700:grid"
              aria-label={
                navOpen
                  ? 'Close navigation'
                  : 'Open navigation'
              }
              aria-expanded={navOpen}
              onClick={() => {
                setMenuOpen(false);
                setNavOpen((current) => !current);
              }}
            >
              {navOpen ? '×' : '☰'}
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-1 block min-h-screen w-full max-w-full flex-auto flex-col" data-scroll-container>
        <main className="relative z-1 block w-full max-w-none flex-auto pb-16 dark:z-2">
          <Outlet />
        </main>
      </div>

      {navOpen && (
        <button
          type="button"
          className="fixed inset-0 z-70 cursor-default bg-transparent from-701:hidden"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      )}
    </div>
  );
}